import { Fragment, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';

import Hero from './Hero';
import ConversationBubble from './ConversationBubble';
import {
    addQuery,
    // fetchAnswer,
    fetchAnswerMindmap,
    selectQueries,
    selectStatus,
    updateQuery
} from './conversationSlice';
import Send from './assets/send.svg';
import Spinner from './assets/spinner.svg';
import { sendFeedback } from './conversationApi';
import ArrowDown from './assets/arrow-down.svg';
import { useMediaQuery } from 'hooks/useMediaQuery';
import Navigation from './Navigation';
import './index.css';
import MarkmapHooks from './markmap-hooks';
import MainCard from 'ui-component/cards/MainCard';
import Avatar from './Avatar';
import DocsGPT3 from './assets/cute_docsgpt3.svg';

import {
    Grid
} from '@mui/material';

import useAuth from 'hooks/useAuth';

// import { Transformer } from "markmap-lib"
// import { Markmap } from "markmap-view"
// import { Toolbar } from "markmap-toolbar"
// import "markmap-toolbar/dist/style.css"

// import {
//   setConversation,
//   updateConversationId
// } from "./conversationSlice"

export default function ConversationMindmap() {
    const queries_mindmap = useSelector(selectQueries);
    const status = useSelector(selectStatus);
    const dispatch = useDispatch();
    const endMessageRef = useRef(null);
    const inputRef = useRef(null);

    const [hasScrolledToLast, setHasScrolledToLast] = useState(true);

    const { isMobile } = useMediaQuery();
    const [navOpen, setNavOpen] = useState(false);
    const { fetchBillings } = useAuth();
    const [isLoading, setLoading] = useState(false);

    const Component = MarkmapHooks;

    // useEffect(() => {
    //   // Create markmap and save to refMm
    //   const mm = Markmap.create(refSvg.current)
    //   refMm.current = mm
    //   renderToolbar(refMm.current, refToolbar.current)
    // }, [refSvg.current])

    // useEffect(() => {
    //   // Update data for markmap once value is changed
    //   const mm = refMm.current
    //   if (!mm) return
    //   const { root } = transformer.transform(value)
    //   mm.setData(root)
    //   mm.fit()
    // }, [refMm.current, value])

    // const handleChange = e => {
    //   setValue(e.target.value)
    // }

    useEffect(() => {
        scrollIntoView();
        fetchBillings();
    }, [queries_mindmap.length, queries_mindmap[queries_mindmap.length - 1]]);

    useEffect(() => {
        const element = document.getElementById('inputbox');
        if (element) {
            element.focus();
        }
    }, []);

    useEffect(() => {
        const observerCallback = (entries) => {
            entries.forEach((entry) => {
                setHasScrolledToLast(entry.isIntersecting);
            });
        };

        const observer = new IntersectionObserver(observerCallback, {
            root: null,
            threshold: [1, 0.8]
        });
        if (endMessageRef.current) {
            observer.observe(endMessageRef.current);
        }

        return () => {
            observer.disconnect();
        };
    }, [endMessageRef.current]);

    const scrollIntoView = () => {
        endMessageRef?.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });
    };

    const handleQuestion = (question) => {
        question = question.trim();
        if (question === '') return;
        setLoading(true);
        dispatch(addQuery({ prompt: question }));
        dispatch(fetchAnswerMindmap({ question }));
        setLoading(false);
    };

    const handleFeedback = (query, feedback, index) => {
        const prevFeedback = query.feedback;
        dispatch(updateQuery({ index, query: { feedback } }));
        sendFeedback(query.prompt, query.response, feedback).catch(() => {
            dispatch(updateQuery({ index, query: { feedback: prevFeedback } }));
        });
    };

    const prepResponseView = (query, index) => {
        let responseView;
        if (query.error) {
            responseView = (
                <ConversationBubble
                    ref={endMessageRef}
                    className={`${index === queries_mindmap.length - 1 ? 'mb-32' : 'mb-7'}`}
                    key={`${index}ERROR`}
                    message={query.error}
                    type="ERROR"
                ></ConversationBubble>
            );
        } else if (!isLoading && query.response) {
            // console.log(query.response)
            responseView = (
                // <ConversationBubble
                //   ref={endMessageRef}
                //   className={`${index === queries_mindmap.length - 1 ? "mb-32" : "mb-7"}`}
                //   key={`${index}ANSWER`}
                //   message={query.response}
                //   type={"ANSWER"}
                //   sources={query.sources}
                //   feedback={query.feedback}
                //   handleFeedback={feedback => handleFeedback(query, feedback, index)}
                // ></ConversationBubble>
                <div className={`flex self-start group flex-col w-full`}>
                    <div className="flex self-start">
                        <Avatar
                            className="mt-2 h-12 w-12 text-2xl"
                            avatar={<img src={DocsGPT3} alt="ailab" className="h-full w-full object-cover" />}
                        />
                        <Component response={query.response} />
                    </div>
                </div>
            );
        }
        return responseView;
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        Document.execCommand('insertText', false, text);
    };

    const theme = useTheme();

    return (
        <MainCard
            sx={{
                bgcolor: 'white'
            }}
            style={{
                height: 1024
            }}
        >
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <div className="min-h-full min-w-full">
                        <Navigation navOpen={navOpen} setNavOpen={setNavOpen} isLoading={isLoading} setLoading={setLoading} />
                        <div
                            className={`transition-all duration-200 ${
                                !isMobile ? `ml-0 ${!navOpen ? '-mt-5 md:mx-auto lg:mx-auto' : 'md:ml-72'}` : 'ml-0 md:ml-16'
                            }`}
                        >
                            <div className="flex flex-col justify-center p-4 md:flex-row  bg-white">
                                {queries_mindmap.length > 0 && !hasScrolledToLast && (
                                    <button
                                        onClick={scrollIntoView}
                                        aria-label="scroll to bottom"
                                        className="fixed bottom-32 right-14 z-10 flex h-7 w-7  items-center justify-center rounded-full border-[0.5px] border-gray-alpha bg-gray-100 bg-opacity-50 md:h-9 md:w-9 md:bg-opacity-100 "
                                    >
                                        <img src={ArrowDown} alt="arrow down" className="h4- w-4 opacity-50 md:h-5 md:w-5" />
                                    </button>
                                )}

                                {queries_mindmap.length > 0 && (
                                    <div className="mt-20 flex flex-col transition-all md:w-full  bg-white h-screen ">
                                        {queries_mindmap.map((query, index) => {
                                            return (
                                                <Fragment key={index}>
                                                    <ConversationBubble
                                                        className={'last:mb-27 mb-7'}
                                                        key={`${index}QUESTION`}
                                                        message={query.prompt}
                                                        type="QUESTION"
                                                        sources={query.sources}
                                                    ></ConversationBubble>
                                                    {prepResponseView(query, index)}
                                                </Fragment>
                                            );
                                        })}
                                    </div>
                                )}
                                {queries_mindmap.length === 0 && (
                                    <Hero className="mt-24 h-[100vh] md:mt-52"></Hero>
                                    // <div className="mt-24 h-[100vh] md:mt-52">
                                    // <div id="customgpt_chat"></div>
                                    // <script src="https://cdn.customgpt.ai/js/embed.js" div_id="customgpt_chat" p_id="16944" p_key="6763c76e45a249745aba49652b26265b" width="100%" height="500px"></script>
                                    // </div>
                                )}
                                <div className="relative bottom-0 flex w-10/12 flex-col items-end self-center pt-3 md:fixed md:w-[65%]">
                                    <div className="flex h-full w-full" style={{ paddingBottom: '30px' }}>
                                        <div
                                            id="inputbox"
                                            ref={inputRef}
                                            tabIndex={1}
                                            placeholder="Type your message here..."
                                            contentEditable
                                            onPaste={handlePaste}
                                            className={`border-000000 overflow-x-hidden; max-h-24 min-h-[2.6rem] w-full overflow-y-auto whitespace-pre-wrap rounded-3xl border bg-white py-2 pl-4 pr-9 text-base leading-7 opacity-100 focus:outline-none`}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    if (inputRef.current?.textContent && status !== 'loading') {
                                                        handleQuestion(inputRef.current.textContent);
                                                        inputRef.current.textContent = '';
                                                    }
                                                }
                                            }}
                                        ></div>
                                        {status === 'loading' ? (
                                            <img
                                                src={Spinner}
                                                className="relative right-[38px] bottom-[7px] -mr-[30px] animate-spin cursor-pointer self-end"
                                            ></img>
                                        ) : (
                                            <div className="relative right-[43px] bottom-[7px] -mr-[35px] h-[35px] w-[35px] cursor-pointer self-end rounded-full hover:bg-gray-3000">
                                                <img
                                                    className="ml-[9px] mt-[9px]"
                                                    onClick={() => {
                                                        if (inputRef.current?.textContent) {
                                                            handleQuestion(inputRef.current.textContent);
                                                            inputRef.current.textContent = '';
                                                        }
                                                    }}
                                                    src={Send}
                                                ></img>
                                            </div>
                                        )}
                                    </div>
                                    {/* <p className="text-gray-595959 w-[100vw] self-center bg-white p-5 text-center text-xs md:w-full">

                  </p> */}
                                </div>
                            </div>
                        </div>
                    </div>
                </Grid>
            </Grid>
        </MainCard>
    );
}
